import { useManualTenderDrawer } from '@/lib/services/cash-drawers/useManualTenderDrawer'
import { useState } from 'react'
import { toast } from 'sonner'

import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import {
  ORDER_ASIDE_RAISED_SURFACE_CLASS,
  ORDER_ASIDE_SUBTLE_SURFACE_CLASS,
  ORDER_ASIDE_WARNING_SURFACE_CLASS,
} from '@/components/orders/order-aside.styles'
import { OrderAsidePanelCard } from '@/components/orders/OrderAsidePanelCard'
import { Button } from '@/components/ui/button'
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import {
  cn,
  formatCurrency,
  generateIdempotencyKey,
  getErrorMessage,
  parseCurrency,
} from '@/lib/utils'
import { useRefundPartialPaymentsMutation } from '@/lib/services/carts/carts.query'
import {
  getCashDrawerOptionLabel,
  manualTenderLocationId,
} from '@/lib/services/cash-drawers/cash-drawers.utils'
import { useAbility } from '@/lib/casl'
import {
  getSalesOrderNetCollectedTotal,
  getSalesOrderRemainingTotal,
} from '@/components/orders/sales-order-presentation'
import {
  type PartialRefundCart,
  buildCartRefundPaymentSignature,
  extractPendingCartRefundIdempotencyKey,
} from '@/components/pos/partial-payment-refund-utils'
import { CashDrawerStatusError } from '@/components/pos/CashDrawerStatusError'
import {
  type ManualTenderDrawer,
  CASH_DRAWER_STATUS_UNAVAILABLE_MESSAGE,
  cashDrawerQueriesAreFetching,
  cashDrawerQueriesHaveError,
  refreshCashDrawerSession,
  refreshManualTenderDrawer,
  retryFailedCashDrawerQueries,
} from '@/lib/services/cash-drawers/cash-drawers.preflight'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import type { SalesOrder } from '@/lib/services/sales-orders/sales-orders.types'
import type { CartRefundPreview } from '@/lib/services/carts/carts.types'

/** The order's payment snapshot with its cart id, as the order page passes it: the fields this form reads. */
interface RefundPaymentState extends PartialRefundCart {
  id?: number | null
  location_id?: number | null
  /** What the refund sends back, by payment, as the server will split it (money rule 4). */
  refund_preview?: CartRefundPreview
}

interface RefundPartialPaymentsFormProps {
  cart: RefundPaymentState
  order: SalesOrder
  onCompleted?: () => void
}

export default function RefundPartialPaymentsForm({
  cart,
  order,
  onCompleted,
}: RefundPartialPaymentsFormProps) {
  const orgId = useRouteOrganizationId()
  const refundPartialPaymentsMutation = useRefundPartialPaymentsMutation()
  const ability = useAbility()
  const canOperateCashDrawers = ability?.can('operate', 'cash-drawers') === true
  const targetLocationId = manualTenderLocationId(order, cart)
  const {
    statusQuery: cashDrawerStatusQuery,
    trackingEnabled: cashDrawerTrackingEnabled,
    workstationsQuery,
    workstationId,
    pairedWorkstation,
    currentDrawerQuery,
    resolvedDrawer: resolvedCashDrawer,
    isLoading: cashDrawerQueriesLoading,
    pairingBlocker,
  } = useManualTenderDrawer({ organizationId: orgId, targetLocationId, canOperateCashDrawers })
  const pendingCartRefundIdempotencyKey = extractPendingCartRefundIdempotencyKey(cart)
  const cartRefundPaymentSignature = buildCartRefundPaymentSignature(cart)
  const refundPreview = cart.refund_preview ?? null
  // Cash and checks to hand back, and what goes back to cards on its own, as the server will split them.
  const manualRefundTotal = parseCurrency(refundPreview?.manual_total ?? 0)
  const automaticRefundTotal = parseCurrency(refundPreview?.automatic_total ?? 0)
  const isCashDrawerLoading = manualRefundTotal > 0 && cashDrawerQueriesLoading
  const cashDrawerStatusUnavailable =
    manualRefundTotal > 0 &&
    cashDrawerQueriesHaveError(cashDrawerStatusQuery, workstationsQuery, currentDrawerQuery)
  const cashDrawerPermissionBlocker =
    "You don't have permission to record a cash or check refund while cash drawer tracking is on."
  const cashDrawerActionBlocked = isCashDrawerLoading || cashDrawerStatusUnavailable
  const collectedTotal = getSalesOrderNetCollectedTotal(order)
  const remainingTotal = getSalesOrderRemainingTotal(order)
  const hasPositivePaymentsAwaitingConfirmation = refundPreview?.payment_in_flight === true
  // The balance is open while the server can still collect on it, as `refund_mode` reads it.
  const canRefundPartialPayments =
    (refundPreview?.payments.length ?? 0) > 0 &&
    collectedTotal > 0 &&
    order.financial_state.can_collect_payment &&
    !hasPositivePaymentsAwaitingConfirmation

  const [isCashDrawerAdmissionPending, setIsCashDrawerAdmissionPending] = useState(false)
  const [refundPartialPaymentsState, setRefundPartialPaymentsState] = useState(() => ({
    idempotencyKey: pendingCartRefundIdempotencyKey,
    paymentSignature: cartRefundPaymentSignature,
  }))

  const cachedRefundPartialPaymentsIdempotencyKey =
    refundPartialPaymentsState.paymentSignature === cartRefundPaymentSignature
      ? refundPartialPaymentsState.idempotencyKey
      : null

  const handleRefundPartialPayments = async () => {
    if (!cart?.id) {
      return false
    }

    setIsCashDrawerAdmissionPending(true)
    let drawerContext: ManualTenderDrawer = {
      trackingEnabled: false,
      session: null,
      workstation: null,
    }
    if (manualRefundTotal > 0) {
      try {
        drawerContext = await refreshManualTenderDrawer({
          canOperateCashDrawers,
          currentDrawerQuery,
          organizationId: orgId,
          pairedWorkstation,
          statusQuery: cashDrawerStatusQuery,
          targetLocationId,
          workstationId,
          workstationsQuery,
        })
      } catch (error) {
        toast.error(
          (error instanceof Error ? error.message : '') ||
            getErrorMessage(error) ||
            CASH_DRAWER_STATUS_UNAVAILABLE_MESSAGE
        )
        setIsCashDrawerAdmissionPending(false)
        return false
      }
    }

    const idempotencyKey =
      pendingCartRefundIdempotencyKey ??
      cachedRefundPartialPaymentsIdempotencyKey ??
      generateIdempotencyKey()

    setRefundPartialPaymentsState({
      idempotencyKey,
      paymentSignature: cartRefundPaymentSignature,
    })

    try {
      const response = await refundPartialPaymentsMutation.mutateAsync({
        cartId: cart.id,
        salesOrderId: order.id,
        idempotencyKey,
        ...(drawerContext.trackingEnabled ? { cashDrawerSessionId: drawerContext.session.id } : {}),
      })

      const summary = response.summary
      const succeededTotal = parseCurrency(summary.succeeded_total)
      const pendingTotal = parseCurrency(summary.pending_total)
      const failedTotal = parseCurrency(summary.failed_total)

      if (failedTotal > 0) {
        toast.warning('Refund completed with issues', {
          description: `Succeeded ${formatCurrency(succeededTotal)}, pending ${formatCurrency(
            pendingTotal
          )}, failed ${formatCurrency(failedTotal)}.`,
        })
      } else if (pendingTotal > 0) {
        toast.success('Refund submitted', {
          description: `Succeeded ${formatCurrency(succeededTotal)} and ${formatCurrency(
            pendingTotal
          )} is pending confirmation.`,
        })
      } else {
        toast.success('Collected payments refunded successfully')
      }

      if (pendingTotal <= 0) {
        setRefundPartialPaymentsState({
          idempotencyKey: null,
          paymentSignature: cartRefundPaymentSignature,
        })
      }

      onCompleted?.()
      return true
    } catch (error) {
      toast.error('Failed to refund collected payments', {
        description: getErrorMessage(error) || 'Please try again',
      })
      if (drawerContext.trackingEnabled) {
        void refreshCashDrawerSession(currentDrawerQuery)
      }
      return false
    } finally {
      setIsCashDrawerAdmissionPending(false)
    }
  }

  return (
    <OrderAsidePanelCard>
      <CardHeader className="space-y-2">
        <CardTitle>Refund payments collected so far</CardTitle>
        <Typography variant="supporting" className="block">
          This order still has an open balance. Refunding here sends back the payments you have
          already collected and leaves the rest of the order unpaid.
        </Typography>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className={cn(ORDER_ASIDE_RAISED_SURFACE_CLASS, 'px-4 py-3')}>
            <Typography variant="eyebrow">Collected so far</Typography>
            <Typography variant="h5" className="mt-2">
              {formatCurrency(collectedTotal)}
            </Typography>
          </div>
          <div className={cn(ORDER_ASIDE_RAISED_SURFACE_CLASS, 'px-4 py-3')}>
            <Typography variant="eyebrow">Balance due</Typography>
            <Typography variant="h5" className="mt-2">
              {formatCurrency(remainingTotal)}
            </Typography>
          </div>
        </div>

        <div className={cn(ORDER_ASIDE_SUBTLE_SURFACE_CLASS, 'px-4 py-3')}>
          <Typography variant="label">What happens next</Typography>
          <div className="mt-2 space-y-2">
            {manualRefundTotal > 0 ? (
              <Typography variant="supporting" className="block">
                {`${formatCurrency(manualRefundTotal)} should be handed back to the customer for cash or check payments.`}
              </Typography>
            ) : null}
            {automaticRefundTotal > 0 ? (
              <Typography variant="supporting" className="block">
                {`${formatCurrency(automaticRefundTotal)} will be refunded back to the original payment methods automatically.`}
              </Typography>
            ) : null}
          </div>
        </div>

        {cashDrawerStatusUnavailable ? (
          <CashDrawerStatusError
            isRetrying={cashDrawerQueriesAreFetching(
              cashDrawerStatusQuery,
              workstationsQuery,
              currentDrawerQuery
            )}
            onRetry={() =>
              retryFailedCashDrawerQueries(
                cashDrawerStatusQuery,
                workstationsQuery,
                currentDrawerQuery
              )
            }
          />
        ) : !isCashDrawerLoading &&
          manualRefundTotal > 0 &&
          cashDrawerTrackingEnabled &&
          canOperateCashDrawers ? (
          <div className="space-y-2">
            <Typography variant="labelStrong" className="block">
              Register
            </Typography>
            {resolvedCashDrawer ? (
              <Typography variant="supporting" className="block">
                {getCashDrawerOptionLabel({
                  workstation: pairedWorkstation,
                  location: pairedWorkstation?.location,
                })}
              </Typography>
            ) : (
              <Typography variant="label" tone="destructive">
                {pairingBlocker}
              </Typography>
            )}
          </div>
        ) : !isCashDrawerLoading && manualRefundTotal > 0 && cashDrawerTrackingEnabled ? (
          <Typography variant="label" tone="destructive">
            {cashDrawerPermissionBlocker}
          </Typography>
        ) : null}

        {hasPositivePaymentsAwaitingConfirmation ? (
          <div className={cn(ORDER_ASIDE_WARNING_SURFACE_CLASS, 'px-4 py-3')}>
            <Typography variant="label" tone="warning">
              A payment is still being confirmed
            </Typography>
            <Typography variant="supporting" className="mt-2 block">
              Wait for the payment confirmation to finish before refunding this order.
            </Typography>
          </div>
        ) : null}

        {!canRefundPartialPayments ? (
          <Typography variant="supporting" className="block">
            There are no collected payments on this open-balance order to refund right now.
          </Typography>
        ) : (
          <ConfirmationDialog
            title="Refund collected payments?"
            description={
              manualRefundTotal > 0 ? (
                <>
                  <span className="block">
                    {`${formatCurrency(manualRefundTotal)} must be handed back to the customer for cash or check payments.`}
                  </span>
                  {automaticRefundTotal > 0 ? (
                    <span className="mt-2 block">
                      {`${formatCurrency(automaticRefundTotal)} will be refunded back to the original payment methods automatically.`}
                    </span>
                  ) : null}
                </>
              ) : (
                'This will refund the payments already collected on this order back to their original payment methods.'
              )
            }
            onConfirm={handleRefundPartialPayments}
            isPending={refundPartialPaymentsMutation.isPending || isCashDrawerAdmissionPending}
            pendingText="Refunding..."
            confirmText="Refund Collected Payments"
            confirmDisabled={cashDrawerActionBlocked || isCashDrawerAdmissionPending}
            trigger={
              <Button
                variant="destructive"
                disabled={
                  refundPartialPaymentsMutation.isPending ||
                  isCashDrawerAdmissionPending ||
                  cashDrawerActionBlocked
                }
              >
                Refund Collected Payments
              </Button>
            }
          />
        )}
      </CardContent>
    </OrderAsidePanelCard>
  )
}
